import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {ActionBox} from 'shells/action-box/action-box';
import {ErrorIcon, SuccessIcon, WarningIcon} from 'shells/action-box/action-icons';
import {Button} from 'shells/button/button';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {ModalBody} from 'shells/typography/modal-body';

import type {CampaignType} from 'nutshell-graphql-types';
import type {EditionFragment as Edition} from 'nutshell-graphql-types';

import {GetSessionUser} from '../../../core/get-session-user';

import {
    SubjectActionBox,
    UnsubscribeActionBox,
    StreetAddressActionBox,
    CustomClickDomainActionBox,
} from '../edition-action-boxes';
import type {ModalComponent} from '../edition-page';
import {DKIMWarning} from '../prepare-to-send-page/dkim-warning';
import {getEditionSecurityInfo} from '../prepare-to-send-page/helpers';

import {AddressModal} from './onboarding-modals/address-modal';
import {UnsubscribeModal} from './onboarding-modals/unsubscribe-modal';

export const MIN_AUDIENCE_SIZE = 20;
export const RECOMMENDED_AUDIENCE_SIZE = 1000;

type Props = {
    edition: Edition;
    streetAddress: string | null | undefined;
    closeAndMoveToView: (newView: string, onboardingModalComponent?: ModalComponent) => void;
    issueCount: number;
    type: CampaignType;
    isLoading: boolean;
    audienceCount: number | null | undefined;
    audienceUnsubcribedRate: number | null | undefined;
};

export function PrepareToSendView(props: Props) {
    const userFacingNoun = props.type === 'NEWSLETTER' ? 'edition' : 'broadcast';

    return (
        <>
            <ModalBody>{getSubtitle(props.issueCount, userFacingNoun)}</ModalBody>
            <GetSessionUser>
                {({user}) => {
                    return props.isLoading ? (
                        <div className='flex full-width full-height align-center justify-center'>
                            <LoadingIcon size={24} />
                        </div>
                    ) : (
                        <div className='full-width'>
                            <StackLegacy spacing={8}>
                                {getFromSection(
                                    props.edition,
                                    props.audienceCount,
                                    () => props.closeAndMoveToView('details'),
                                    userFacingNoun,
                                    <ErrorIcon />,
                                    Boolean(user && user.permissions.canAccessSetup)
                                )}
                                {getToSection(
                                    props.edition,
                                    props.audienceCount,
                                    () => props.closeAndMoveToView('details'),
                                    userFacingNoun,
                                    <ErrorIcon />,
                                    <SuccessIcon />,
                                    <WarningIcon />
                                )}
                                <SubjectActionBox
                                    edition={props.edition}
                                    onClickActionItem={() => {
                                        props.closeAndMoveToView('details');
                                    }}
                                />
                                <UnsubscribeActionBox
                                    edition={props.edition}
                                    onClickActionItem={() => {
                                        props.closeAndMoveToView('editor', UnsubscribeModal);
                                    }}
                                />
                                <StreetAddressActionBox
                                    edition={props.edition}
                                    streetAddress={props.streetAddress}
                                    canAccessSetup={Boolean(
                                        user && user.permissions.canAccessSetup
                                    )}
                                    onClickActionItem={() => {
                                        props.closeAndMoveToView('editor', AddressModal);
                                    }}
                                />
                                {props.edition.customClickDomain &&
                                    !props.edition.customClickDomain.isVerified && (
                                        <CustomClickDomainActionBox
                                            canAccessSetup={Boolean(
                                                user && user.permissions.canAccessSetup
                                            )}
                                        />
                                    )}
                            </StackLegacy>
                        </div>
                    );
                }}
            </GetSessionUser>
            {getWarningBeforeSending(
                props.edition,
                props.audienceCount,
                props.audienceUnsubcribedRate
            )}
        </>
    );
}

function getToSection(
    edition: Edition,
    audienceCount: number | null | undefined,
    onGoToDetails: () => void,
    userFacingNoun: string,
    exMark: React.ReactElement<any>,
    checkmark: React.ReactElement<any>,
    warningIcon: React.ReactElement<any>
) {
    if (!edition.audience) {
        // No audience selected
        return (
            <ActionBox
                size='large'
                title='To'
                subtitle='Select an audience for this message'
                actionElement={
                    <Button variant='text-primary' size='big' onClick={onGoToDetails}>
                        Go to {userFacingNoun} details
                    </Button>
                }
                icon={exMark}
            />
        );
    } else if (edition.isABTesting && audienceCount && audienceCount < MIN_AUDIENCE_SIZE) {
        // Audience is too small for A/B tests
        return (
            <ActionBox
                size='large'
                title='To'
                subtitle='Your audience is too small to send. 20 or more recipients are required for A/B tests.'
                actionElement={<div />}
                icon={exMark}
            />
        );
    } else if (edition.isABTesting && audienceCount && audienceCount < RECOMMENDED_AUDIENCE_SIZE) {
        // Audience is smaller than recommended
        return (
            <ActionBox
                size='large'
                title='To'
                subtitle='For better test results, consider sending to an audience of over 1,000 people.'
                actionElement={<div />}
                icon={warningIcon}
            />
        );
    } else if (audienceCount && audienceCount > 0) {
        // Audience is sufficient
        return (
            <ActionBox
                size='large'
                title='To'
                subtitle={`This message will be sent to ${audienceCount} ${
                    audienceCount === 1 ? 'subscriber' : 'subscribers'
                }`}
                actionElement={<div />}
                icon={checkmark}
            />
        );
    }

    // Otherwise return nothing
    return undefined;
}

function getFromSection(
    edition: Edition,
    audienceCount: number | null | undefined,
    onGoToDetails: () => void,
    userFacingNoun: string,
    exMark: React.ReactElement<any>,
    canAccessSetup: boolean
) {
    if (!edition.from) {
        // No sender selected
        return (
            <ActionBox
                size='large'
                title='From'
                subtitle='Select a sender for this message'
                actionElement={
                    <Button variant='text-primary' size='big' onClick={onGoToDetails}>
                        Go to {userFacingNoun} details
                    </Button>
                }
                icon={exMark}
            />
        );
    } else if (edition.from && edition.from.status !== 'VERIFIED') {
        // They have a from, but it’s still unverified (only admins can access setup)
        // TODO: Update this when non-admins can access marketing settings
        return (
            <ActionBox
                size='large'
                title='From'
                subtitle='Your selected sender address hasn’t been verified'
                actionElement={
                    canAccessSetup ? (
                        <Link
                            variant='primary'
                            to='/email-marketing/sender-address'
                            // @ts-expect-error
                            as={RoutedLink}
                        >
                            Manage sender addresses
                        </Link>
                    ) : undefined
                }
                icon={exMark}
            />
        );
    } else if (edition.from) {
        const editionFrom = edition.from;
        const editionSecurityInfo = getEditionSecurityInfo(editionFrom);

        return (
            <DKIMWarning
                editionFrom={editionFrom}
                canAccessSetup={canAccessSetup}
                userFacingNoun={userFacingNoun}
                editionSecurityInfo={editionSecurityInfo}
            />
        );
    }

    // Otherwise return nothing
    return undefined;
}

// @ts-expect-error
function getSubtitle(issueCount, userFacingNoun): string {
    if (issueCount === 0) {
        return `This ${userFacingNoun} has all required items`;
    }

    if (issueCount === 1) {
        return `This ${userFacingNoun} is still missing a required item`;
    }

    return `This ${userFacingNoun} is still missing a few required items`;
}

function getWarningBeforeSending(
    edition: Edition,
    audienceCount?: number | null,
    audienceUnsubcribedRate?: number | null
): React.ReactElement<any> | null | undefined {
    if (
        audienceCount &&
        audienceCount > 500 &&
        typeof audienceUnsubcribedRate === 'number' &&
        audienceUnsubcribedRate < 0.01
    ) {
        return (
            <FlashBanner type='warning' hasBorder={true} noRightPadding={true}>
                <div className='flex justify-sb align-center'>
                    <div style={{maxWidth: '75%'}}>
                        <span style={{fontWeight: 'bold'}}>Heads up!</span> We recommend importing
                        unsubscribers into Nutshell before sending this broadcast, or your message
                        might result in a high bounce rate.
                    </div>
                    <Link
                        variant='primary'
                        href='https://support.nutshell.com/en/articles/8428874-audiences-unsubscribe-contacts-from-campaigns'
                        newTab={true}
                    >
                        Learn more
                    </Link>
                </div>
            </FlashBanner>
        );
    }

    if (edition.hasWebFonts) {
        return (
            <FlashBanner type='info' hasBorder={true} noRightPadding={true}>
                <div className='flex justify-sb full-width'>
                    This message uses a web font that may appear differently in some inboxes
                    <Link
                        variant='primary'
                        href='https://support.nutshell.com/en/articles/8428876-campaigns-which-fonts-can-you-use'
                        newTab={true}
                    >
                        Learn more
                    </Link>
                </div>
            </FlashBanner>
        );
    }

    return null;
}
